import { Component, ViewChild, AfterViewInit, OnInit, inject } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltip } from "@angular/material/tooltip";
import { CommonModule } from '@angular/common';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { AdminService } from '@adgate/modules/admin/services/admin-service';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { AdminPanelModule } from '@adgate/modules/admin/types/admin-panel-module.interface';
import { User } from '@adgate/modules/admin/types/user.type';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { filter, switchMap } from 'rxjs';
import { UserEditDialogComponent } from '@adgate/modules/admin/dialogs/user-edit-dialog-component/user-edit-dialog-component';
import { QuickSearch } from '@adgate/common/interfaces/quick-search.interface';


@Component({
  selector: 'app-admin-panel-users-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatTooltip,
    ObjectDescPipe
  ],
  templateUrl: './admin-panel-users-component.html',
  styleUrl: './admin-panel-users-component.scss',
})
export class AdminPanelUsersComponent implements OnInit, AfterViewInit, AdminPanelModule, QuickSearch
{
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private authService: AuthenticationService = inject(AuthenticationService);
  private adminService: AdminService = inject(AdminService);
  private dialogService: DialogService = inject(DialogService);

  displayedColumns: string[] = ['name', 'email', 'authType', 'office', 'agency', 'lastLogin', 'actions'];
  dataSource: MatTableDataSource<User> = new MatTableDataSource<User>();

  constructor()
  {
    
  }

  ngOnInit(): void
  {
    this.onRefresh();
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onRefresh()
  {
    let attr = this.authService.getUserAttributes();

    this.dialogService.showProgress();
    this.adminService.getUsers((attr.type == User.TYPE_MULTI ? null : attr.office.id)).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.hideProgress();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  getQuickSearchPlaceholder(): string 
  {
    return 'Search Users';
  }

  onQuickSearch(filter: string)
  {
    this.dataSource.filter = filter;
  }

  onNewUser(): void
  {
    this.onEditUser( new User() );
  }

  onEditUser(user: User): void
  {
    let successMessage: string;
    let attr = this.authService.getUserAttributes();

    const dialogRef = this.dialogService.openAnyDialog(UserEditDialogComponent, {
      data: user,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: User | null) => result !== null),
      switchMap((result: User) => {
        this.dialogService.showProgress();
        return this.adminService.updateUser(result);
      }),
      switchMap((r: RESTApplicationResponse) => {
        successMessage = r.message;
        return this.adminService.getUsers((attr.type == User.TYPE_MULTI ? null : attr.office.id));
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.openInfoDialog(successMessage);
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onDeleteUser(row: User): void
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Delete user "${row.fullName}"?`,
      {
        title: 'Delete User',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'danger'
      }
    );

    dialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed === true),
      switchMap(() => {
        this.dialogService.showProgress();
        return this.adminService.deleteUser(row.id);
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.dialogService.openInfoDialog(r.message);
        this.onRefresh();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  decodeAuthType(row: User): string
  {
      switch( row.authMethod ) {
          case 1: return 'Internal';
          case 3: return 'MyID';
          default: return 'N/A';
      }
  }

  getTableDataAccessor(): any
  {
    return (item: any, property: any) => {
      switch(property) {
        case 'name': return item.fullName;
        case 'authType': return item.authMethod;
        case 'office': return (item.office != null ? item.office.description : '');
        case 'agency': return (item.agency != null ? item.agency.description : '');
        default: return item[property];
      }
    };
  }
}
